import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { flushSync } from "react-dom";
import { Divider, Grid } from "@mui/material";
import { StriimTypography, StriimTheme, StriimButton } from "@striim/striim-ui";
import HighchartsReact from "highcharts-react-official";
import * as Highcharts from "highcharts/react/highstock";

import { formatTimestampForTooltip } from "./utils";
import { ArrowForwardBlue } from "../../../../../generic/icon/customIcons";
import { commonOptions, CustomLegend, CustomPlotbandTooltip, DataLegend, eventsChartColors } from "./constants";
import {
    AllSentinelsSDIDrillDownEventsChartData,
    EventsProcessedChartData,
    LegendData
} from "../../../guardian/sentinel-monitor/service/sentinel-monitor.types";

const CustomTooltip = ({ points, chart, dataUnavailableTimestamp }) => {
    const xValue = formatTimestampForTooltip(points[0].x);
    const sdiValue = points[2].y;
    const sensitiveValue = points[1].y;
    const nonSensitiveValue = points[0].y;

    if (dataUnavailableTimestamp && dataUnavailableTimestamp < points[0].x) {
        chart.tooltip.label?.element ? (chart.tooltip.label.element.style.display = "none") : "";
        return;
    } else {
        chart.tooltip.label?.element ? (chart.tooltip.label.element.style.display = "block") : "";
    }

    return (
        <Grid container minWidth={230} mt={2.5}>
            <StriimTypography variant="caption4" color="greyscale.700" fontFamily={"Inter"} p={1}>
                {xValue}
            </StriimTypography>
            <Divider
                variant="fullWidth"
                sx={{ width: "100%", borderColor: "greyscale.200", width: "calc(100% + 16px)", marginLeft: -1 }}
            />
            <Grid container p={1}>
                <Grid display="flex" width={"100%"} item justifyContent={"space-between"} alignItems={"center"}>
                    <Grid item display="flex" alignItems={"center"} gap={0.5}>
                        <CustomLegend color={eventsChartColors.sdiBarColor} />
                        <StriimTypography variant="caption3" color="greyscale.700" fontFamily={"Inter"}>
                            Sensitive Data W/ Identifier
                        </StriimTypography>
                    </Grid>
                    <StriimTypography variant="caption3" color="primary.700" fontFamily={"Inter"}>
                        {sdiValue}
                    </StriimTypography>
                </Grid>
                <Grid display="flex" width={"100%"} item justifyContent={"space-between"} alignItems={"center"}>
                    <Grid item display="flex" alignItems={"center"} gap={0.5}>
                        <CustomLegend color={eventsChartColors.sensitiveBarColor} />
                        <StriimTypography variant="caption3" color="greyscale.700" fontFamily={"Inter"}>
                            Sensitive Data W/O Identifier
                        </StriimTypography>
                    </Grid>
                    <StriimTypography variant="caption3" color="primary.700" fontFamily={"Inter"}>
                        {sensitiveValue}
                    </StriimTypography>
                </Grid>
                <Grid display="flex" width={"100%"} item justifyContent={"space-between"} alignItems={"center"}>
                    <Grid item display="flex" alignItems={"center"} gap={0.5}>
                        <CustomLegend color={eventsChartColors.nonSensitiveBarColor} />
                        <StriimTypography variant="caption3" color="greyscale.700" fontFamily={"Inter"}>
                            W/O Sensitive Data
                        </StriimTypography>
                    </Grid>
                    <StriimTypography variant="caption3" color="primary.700" fontFamily={"Inter"}>
                        {nonSensitiveValue}
                    </StriimTypography>
                </Grid>
            </Grid>
        </Grid>
    );
};

interface SDIEventsChartProps {
    chartRef: object;
    chartData: EventsProcessedChartData | AllSentinelsSDIDrillDownEventsChartData | null;
    onScroll: Function;
    hideNavigator?: boolean;
    selectedDateRange: { from: number; to: number } | null;
    isDiscoveryDisabled?: boolean;
    hasAppStopped?: boolean;
    dataUnavailableTimestamp?: number | null;
    onLiveViewClick: Function;
    legendData: LegendData;
}

const SDIEventsChart: React.FC<SDIEventsChartProps> = ({
    chartRef,
    chartData,
    onScroll,
    hideNavigator = false,
    selectedDateRange,
    isDiscoveryDisabled = false,
    hasAppStopped = false,
    dataUnavailableTimestamp,
    legendData,
    onLiveViewClick
}) => {
    const [chartOptions, setChartOptions] = useState(null);
    const [plotBand, setPlotBand] = useState(null);

    useEffect(() => {
        const options = {
            chart: commonOptions.chart,
            credits: commonOptions.credits,
            series: [
                {
                    name: "W/O Sensitive Data",
                    color: eventsChartColors.nonSensitiveBarColor,
                    type: "column",
                    data: chartData.nonSensitiveData
                },
                {
                    name: "W/ Sensitive Data",
                    data: chartData.sensitiveData,
                    type: "column",
                    color: eventsChartColors.sensitiveBarColor
                },
                {
                    name: "Events with SDI",
                    data: chartData.identifierSensitiveData,
                    type: "column",
                    color: eventsChartColors.sdiBarColor
                }
            ],
            plotOptions: commonOptions.plotOptions,
            xAxis: {
                ...commonOptions.xAxis,
                events: {
                    setExtremes: function(event) {
                        const dateRange = event.max !== this.dataMax ? { from: event.min, to: event.max } : null;
                        onScroll(event, dateRange);
                    }
                },
                range: hideNavigator ? 3600 * 24 * 1000 : 3600 * 1000 //hideNavigator = true -> 24 hour chart, 3600 = 1hr - all in milliseconds
            },
            yAxis: commonOptions.yAxis,
            scrollbar: { enabled: !hideNavigator },
            navigator: {
                ...commonOptions.navigator,
                enabled: !hideNavigator,
                series: {
                    lineColor: eventsChartColors.sdiBarColor,
                    fillOpacity: 0,
                    data: chartData.identifierSensitiveData
                }
            },
            rangeSelector: commonOptions.rangeSelector,
            tooltip: {
                ...commonOptions.tooltip,
                formatter: function() {
                    const chart = chartRef.current.chart;
                    const tooltipDiv = document.createElement("div");
                    const root = ReactDOM.createRoot(tooltipDiv);
                    flushSync(() => {
                        root.render(
                            <StriimTheme preselector=".striim.materialize.light:not(#\20)">
                                <CustomTooltip
                                    points={this.points}
                                    chart={chart}
                                    dataUnavailableTimestamp={dataUnavailableTimestamp}
                                />
                            </StriimTheme>
                        );
                    });
                    return tooltipDiv.innerHTML;
                }
            }
        };
        setChartOptions(options);
    }, [chartData, hideNavigator, dataUnavailableTimestamp]);

    useEffect(() => {
        const updateChart = () => {
            const chart = chartRef?.current?.chart;
            if (!chart) return;
            chart.xAxis[0].setExtremes(null, null, false);
            chart.redraw();
        };
        updateChart();
    }, [hideNavigator]);

    useEffect(() => {
        if (chartRef.current?.chart?.xAxis) {
            const chart = chartRef.current.chart;
            if (isDiscoveryDisabled || hasAppStopped) {
                if (!plotBand?.id) {
                    const id = "custom-plot-band";
                    chart.xAxis[0].addPlotBand({
                        from: dataUnavailableTimestamp,
                        to: chartData.sensitiveData[chartData.sensitiveData.length - 1][0],
                        color: "#EBF2FA",
                        id: id,
                        events: {
                            mouseover: function(event) {
                                const xValue = chart.xAxis[0].toValue(event.clientX);
                                const plotbandEl = document.getElementById("custom-plot-band");
                                if (plotbandEl) {
                                    plotbandEl.style.display = "block";
                                    setPlotBand(prev => ({ ...prev, x: xValue }));
                                }
                            },
                            mouseout: function() {
                                const plotbandEl = document.getElementById("custom-plot-band");
                                if (plotbandEl) {
                                    plotbandEl.style.display = "none";
                                }
                            }
                        }
                    });
                    setPlotBand(prev => ({ ...prev, id }));
                }
            } else if (plotBand?.id) {
                setPlotBand({ id: null });
                setChartOptions(prevOptions => ({
                    ...prevOptions,
                    xAxis: {
                        ...prevOptions.xAxis,
                        plotBands: []
                    }
                }));
            }
        }
    }, [isDiscoveryDisabled, hasAppStopped, plotBand, dataUnavailableTimestamp, chartData]);

    const scrollToLatest = () => {
        const chart = chartRef.current.chart;
        const xAxis = chart.xAxis[0];
        xAxis.setExtremes(null, null, false);
        chart.reflow();
    };

    return (
        <div>
            <DataLegend data={legendData} showAmount={true} />
            <HighchartsReact
                ref={chartRef}
                highcharts={Highcharts}
                constructorType={"stockChart"}
                options={chartOptions}
            />
            {!!selectedDateRange && !hideNavigator ? (
                <StriimButton
                    onClick={() => {
                        onLiveViewClick();
                        scrollToLatest();
                    }}
                    variant="text"
                    endIcon={<ArrowForwardBlue />}
                    sx={{ position: "absolute", right: 0, bottom: "-25px" }}
                >
                    Live View
                </StriimButton>
            ) : null}
            <div id="custom-plot-band" style={{ position: "absolute", top: -70, right: 0 }}>
                {plotBand?.x ? (
                    <CustomPlotbandTooltip
                        x={plotBand?.x}
                        isDiscoveryDisabled={isDiscoveryDisabled}
                        hasAppStopped={hasAppStopped}
                    />
                ) : null}
            </div>
        </div>
    );
};

export default SDIEventsChart;
